/**
 * 珊瑚礁明暗两套底色 token。
 *
 * 两组必须保持完全相同的键集合：`setAntdConfig` 对 token 是合并语义，
 * 删除键并不会让 antd 回退到算法默认值，只有显式覆盖才会生效。
 * 因此切换主题时是整组替换，而不是「深色时加、浅色时删」。
 */
type ThemeSurfaceTokens = {
  colorPrimary: string;
  colorInfo: string;
  colorLink: string;
  colorPrimaryBg: string;
  colorPrimaryBgHover: string;
  colorPrimaryBorder: string;
  colorPrimaryBorderHover: string;
  colorPrimaryHover: string;
  colorPrimaryActive: string;
  colorPrimaryTextHover: string;
  colorPrimaryText: string;
  colorPrimaryTextActive: string;
  controlItemBgActive: string;
  controlItemBgActiveHover: string;
  colorBgBase: string;
  colorBgLayout: string;
  colorBgContainer: string;
  colorBgElevated: string;
  colorBorder: string;
  colorBorderSecondary: string;
  colorTextBase: string;
  colorText: string;
  colorTextSecondary: string;
  colorTextTertiary: string;
  colorTextQuaternary: string;
  colorTextHeading: string;
  colorFillQuaternary: string;
};

/** 深色：极夜纯黑 + 纯净皓白高光。 */
export const CORAL_DARK_TOKENS: ThemeSurfaceTokens = {
  colorPrimary: '#F4F4F5',
  colorInfo: '#1668DC',
  colorLink: '#F4F4F5',
  colorPrimaryBg: '#27272A',
  colorPrimaryBgHover: '#3F3F46',
  colorPrimaryBorder: '#3F3F46',
  colorPrimaryBorderHover: '#52525B',
  colorPrimaryHover: '#FFFFFF',
  colorPrimaryActive: '#E4E4E7',
  colorPrimaryTextHover: '#FFFFFF',
  colorPrimaryText: '#F4F4F5',
  colorPrimaryTextActive: '#E4E4E7',
  controlItemBgActive: '#27272A',
  controlItemBgActiveHover: '#3F3F46',
  colorBgBase: '#121212',
  colorBgLayout: '#121212',
  colorBgContainer: '#121212',
  colorBgElevated: '#1E1E1E',
  colorBorder: '#27272A',
  colorBorderSecondary: '#1E1E22',
  colorTextBase: '#FFFFFF',
  colorText: 'rgba(255, 255, 255, 0.90)',
  colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
  colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
  colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
  colorTextHeading: 'rgba(255, 255, 255, 0.92)',
  colorFillQuaternary: 'rgba(255, 255, 255, 0.06)',
};

/** 浅色：极简冷灰画布 + 曜石纯黑主色。 */
export const CORAL_LIGHT_TOKENS: ThemeSurfaceTokens = {
  colorPrimary: '#18181B',
  colorInfo: '#1677FF',
  colorLink: '#18181B',
  colorPrimaryBg: '#F4F4F5',
  colorPrimaryBgHover: '#E4E4E7',
  colorPrimaryBorder: '#E4E4E7',
  colorPrimaryBorderHover: '#D4D4D8',
  colorPrimaryHover: '#27272A',
  colorPrimaryActive: '#09090B',
  colorPrimaryTextHover: '#27272A',
  colorPrimaryText: '#18181B',
  colorPrimaryTextActive: '#09090B',
  controlItemBgActive: '#F4F4F5',
  controlItemBgActiveHover: '#E4E4E7',
  colorBgBase: '#FFFFFF',
  colorBgLayout: '#FFFFFF',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBorder: '#E4E4E7',
  colorBorderSecondary: '#F4F4F5',
  colorTextBase: '#000000',
  colorText: 'rgba(0, 0, 0, 0.88)',
  colorTextSecondary: 'rgba(0, 0, 0, 0.65)',
  colorTextTertiary: 'rgba(0, 0, 0, 0.45)',
  colorTextQuaternary: 'rgba(0, 0, 0, 0.25)',
  colorTextHeading: 'rgba(0, 0, 0, 0.88)',
  colorFillQuaternary: 'rgba(0, 0, 0, 0.04)',
};

/**
 * 主色实心控件上的文字色。antd 默认用白字，深色主题的主色接近白色，
 * 白字会看不见，所以深色时改为深色文字。两组键集合同样必须一致。
 */
export const CORAL_DARK_COMPONENTS = {
  Button: { primaryColor: '#09090B' },
  Radio: { buttonSolidCheckedColor: '#09090B' },
};

export const CORAL_LIGHT_COMPONENTS: typeof CORAL_DARK_COMPONENTS = {
  Button: { primaryColor: '#FFFFFF' },
  Radio: { buttonSolidCheckedColor: '#FFFFFF' },
};

/** 页面画布底色，需与 global.less 中的同名取值保持一致。 */
export const CORAL_CANVAS = {
  dark: CORAL_DARK_TOKENS.colorBgLayout,
  light: CORAL_LIGHT_TOKENS.colorBgLayout,
};

/** Banner 高度；src/components/Layout/layoutConstants.ts 由此派生。 */
export const HEADER_HEIGHT = 44;

/**
 * Banner 与 Sidebar 共用的导航配色，需与 src/styles/shell.less 中的
 * --app-nav-* 变量保持一致。
 */
const NAVIGATION_PALETTE = {
  dark: {
    background: '#1E1E1E',
    text: 'rgba(255, 255, 255, 0.88)',
    muted: 'rgba(255, 255, 255, 0.65)',
    hoverBackground: 'rgba(255, 255, 255, 0.08)',
    selectedBackground: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    background: '#F0F0F3',
    text: '#1F2633',
    muted: '#667085',
    hoverBackground: 'rgba(0, 0, 0, 0.04)',
    selectedBackground: 'rgba(0, 0, 0, 0.06)',
  },
};

/**
 * 从唯一主题状态生成完整的 ProLayout header / sider token。
 * 只换背景而让文字继承旧主题，会出现浅底白字或深底黑字。
 */
export const getNavigationToken = (navTheme?: string) => {
  const palette =
    navTheme === 'realDark'
      ? NAVIGATION_PALETTE.dark
      : NAVIGATION_PALETTE.light;

  return {
    header: {
      heightLayoutHeader: HEADER_HEIGHT,
      colorBgHeader: palette.background,
      colorBgScrollHeader: palette.background,
      colorBgMenuElevated: palette.background,
      colorHeaderTitle: palette.text,
      colorTextMenu: palette.muted,
      colorTextMenuSecondary: palette.muted,
      colorTextMenuActive: palette.text,
      colorTextMenuSelected: palette.text,
      colorBgMenuItemHover: 'transparent',
      colorBgMenuItemSelected: 'transparent',
      colorTextRightActionsItem: palette.text,
      colorBgRightActionsItemHover: 'transparent',
    },
    sider: {
      colorMenuBackground: palette.background,
      colorBgCollapsedButton: palette.background,
      colorTextCollapsedButton: palette.muted,
      colorTextCollapsedButtonHover: palette.text,
      colorBgMenuItemCollapsedElevated: palette.background,
      colorMenuItemDivider: 'transparent',
      colorTextMenu: palette.muted,
      colorTextMenuSecondary: palette.muted,
      colorTextMenuTitle: palette.text,
      colorTextMenuActive: palette.text,
      colorTextMenuItemHover: palette.text,
      colorTextMenuSelected: palette.text,
      colorTextSubMenuSelected: palette.text,
      colorBgMenuItemHover: palette.hoverBackground,
      colorBgMenuItemActive: palette.selectedBackground,
      colorBgMenuItemSelected: palette.selectedBackground,
      paddingInlineLayoutMenu: 0,
      paddingBlockLayoutMenu: 0,
    },
  };
};

/**
 * @zhiyue/core 持久化 navTheme 用的 localStorage key，改动需与该包同步。
 * 该包在读不到值时回退为 light，因此要让珊瑚礁默认深色，必须在首次进入时补写。
 */
const NAV_THEME_STORAGE_KEY = 'app_nav_theme';

/** 首次访问（无历史选择）时把默认主题落成深色；已有选择则完全尊重用户偏好。 */
export const ensureDefaultNavTheme = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!window.localStorage.getItem(NAV_THEME_STORAGE_KEY)) {
      window.localStorage.setItem(NAV_THEME_STORAGE_KEY, 'realDark');
    }
  } catch {
    // 隐私模式等场景下 localStorage 不可写，此时退化为 @zhiyue/core 的默认浅色即可
  }
};

/**
 * 按 @zhiyue/core 的口径读取当前 navTheme。
 *
 * ProLayout 会依据 initialState.settings.navTheme 再套一层 ConfigProvider，
 * 若它与 @zhiyue/core 的偏好不同步，就会出现「外层浅色、内层深色」的错乱，
 * 因此初始 settings 必须与该包读同一份存储。
 */
export const getStoredNavTheme = (): 'realDark' | 'light' => {
  if (typeof window === 'undefined') {
    return 'realDark';
  }

  try {
    return window.localStorage.getItem(NAV_THEME_STORAGE_KEY) === 'realDark'
      ? 'realDark'
      : 'light';
  } catch {
    return 'light';
  }
};
